/**
 * Figma: SINGLE-SKILL-DATABASE — Student Portal header (node 19359-105614)
 * @see https://www.figma.com/design/45iMrqwOg2l7MdJD98RgHg/SINGLE-SKILL-DATABASE--Updated-Design--SEPTEMBER?node-id=19359-105614
 */

/** Top bar background — Figma / Student Portal spec */
export const PORTAL_HEADER_BG = "#FFF8FA";

/** Main content horizontal inset (Dashboard, header, etc.) */
export const PORTAL_CONTENT_PADDING_X = "px-6";

/** Top bar above main content (right of sidebar) — see .app-header in index.css */
export const PORTAL_HEADER_SHELL_CLASS = "app-header";

export const PORTAL_HEADER_TITLE_CLASS = "header-branding__title";

export const PORTAL_HEADER_ICON_PX = 24;

/** Right-side pills — white fill + light grey stroke */
export const PORTAL_HEADER_CHIP_BORDER = "border border-gray-200";

export const PORTAL_HEADER_CHIP_CLASS =
	`bg-white shadow-none transition-colors duration-150 hover:bg-gray-50 ${PORTAL_HEADER_CHIP_BORDER}`;

/** Form section cards (preferences, profile-style flows) */
export const PORTAL_FORM_SECTION_CARD_CLASS =
	"w-full rounded-lg border border-gray-200 bg-white p-6";

/** Profile overlay — wider than legacy 35% for form fields */
export const PORTAL_PROFILE_MODAL_WIDTH_PERCENT = 42;

/** Preferences / location modal — matches institute-style wide dialogs */
export const PORTAL_PREFERENCES_MODAL_WIDTH_CLASS = "max-w-[55rem] w-full min-w-0";

/** Job offer / interview detail modal */
export const PORTAL_JOB_OFFER_MODAL_WIDTH_CLASS = "w-full max-w-4xl min-w-0";

/** Jobs list filter modal */
export const PORTAL_JOBS_FILTER_MODAL_WIDTH_CLASS = "w-full max-w-5xl min-w-0";

/** Employment status modal (Employed / Self Employed / Higher Studies) */
export const PORTAL_STATUS_MODAL_WIDTH_CLASS = "w-full max-w-5xl min-w-0";
export const PORTAL_PREFERENCES_MODAL_HEIGHT_CLASS = "max-h-[min(90vh,52rem)]";

export const PORTAL_HEADER_CHIP_ROUNDED = "rounded-full";

/** Header action pills (status, bell, profile) — 3rem, matches Employer Portal */
export const PORTAL_HEADER_CHIP_HEIGHT_CLASS = "h-12";

/** Gap between status, bell, and profile (Figma: 8px) */
export const PORTAL_HEADER_ACTIONS_CLASS = "flex shrink-0 items-center gap-2";

/** Status pill — green dot + label + chevron */
export const PORTAL_HEADER_STATUS_CHIP_CLASS = `flex ${PORTAL_HEADER_CHIP_HEIGHT_CLASS} items-center gap-2 ${PORTAL_HEADER_CHIP_ROUNDED} px-4 ${PORTAL_HEADER_CHIP_CLASS}`;

/** Notification — circular white pill */
export const PORTAL_HEADER_BELL_WRAPPER_CLASS = `flex ${PORTAL_HEADER_CHIP_HEIGHT_CLASS} w-14 shrink-0 items-center justify-center ${PORTAL_HEADER_CHIP_ROUNDED} ${PORTAL_HEADER_CHIP_CLASS}`;

/** Profile pill — avatar + chevron */
export const PORTAL_HEADER_PROFILE_CHIP_CLASS = `flex ${PORTAL_HEADER_CHIP_HEIGHT_CLASS} items-center gap-1.5 ${PORTAL_HEADER_CHIP_ROUNDED} pl-1 pr-2.5 ${PORTAL_HEADER_CHIP_CLASS}`;

/** Status availability indicator */
export const PORTAL_HEADER_STATUS_DOT_CLASS = "h-2.5 w-2.5 shrink-0 rounded-full bg-green-500";

/** Jobs filter modal — text inputs (City, Salary) */
export const PORTAL_FORM_FILTER_CONTROL_HEIGHT = 40;

export const PORTAL_FORM_FILTER_CONTROL_HEIGHT_CLASS = "h-[40px] min-h-[40px]";

/** Jobs filter modal — closed multi-select triggers (Job Type, Sector, Trade) */
export const PORTAL_FORM_FILTER_SELECT_CONTROL_HEIGHT = 38;

/** Chevron cap for filter multi-selects — keep in sync with control height */
export const PORTAL_FORM_FILTER_SELECT_CHEVRON = {
	size: 28,
	icon: 14,
	rightInset: 4,
	borderRadius: 4,
} as const;

export const PORTAL_FORM_FILTER_INPUT_CLASS = `w-full min-w-0 ${PORTAL_FORM_FILTER_CONTROL_HEIGHT_CLASS} box-border rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-[13px] text-gray-700 placeholder:text-[13px] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent`;

/**
 * Courses row — search & organization (Figma nodes 19368-111207, 19363-110474)
 * @see https://www.figma.com/design/45iMrqwOg2l7MdJD98RgHg/SINGLE-SKILL-DATABASE--Updated-Design--SEPTEMBER?node-id=19368-111207
 */
/** Dashboard courses row — matches Jobs filter Job Type / select height (38px) */
export const PORTAL_COURSE_FIELD_HEIGHT_CLASS = "h-[38px] min-h-[38px]";

export const PORTAL_COURSE_FIELD_WIDTH_CLASS = "w-full max-w-[248px]";

export const PORTAL_COURSE_FIELDS_ROW_GAP_CLASS = "gap-[14px]";

export const PORTAL_COURSE_FIELD_SHELL_CLASS = `flex ${PORTAL_COURSE_FIELD_HEIGHT_CLASS} ${PORTAL_COURSE_FIELD_WIDTH_CLASS} shrink-0 items-center gap-[6px] overflow-hidden rounded-[6px] border border-[rgba(157,157,157,0.5)] bg-white py-[3px] pl-[8px] pr-[3px] focus-within:border-accent/50 focus-within:ring-2 focus-within:ring-accent/15`;

export const PORTAL_COURSE_FIELD_INPUT_CLASS =
	"min-w-0 flex-1 h-full border-0 bg-transparent text-[13px] font-normal leading-5 text-primary outline-none placeholder:text-[13px] placeholder:text-[rgba(22,22,22,0.5)]";

/** Inset maroon action tile (#ba2e5d) — search icon / org chevron */
export const PORTAL_COURSE_FIELD_ACTION_BTN_CLASS =
	"flex h-[28px] w-[32px] shrink-0 items-center justify-center rounded-[4px] bg-[#ba2e5d] p-[6px] transition-colors hover:bg-sidebar-header focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40";

export const PORTAL_COURSE_FIELD_ACTION_ICON_SIZE = 16;

/** Organization filter — same height as Jobs filter Job Type select (38px) */
export const PORTAL_COURSE_ORG_SELECT_HEIGHT = PORTAL_FORM_FILTER_SELECT_CONTROL_HEIGHT;

/** Set Preference — row-aligned with course filters */
export const PORTAL_SET_PREFERENCE_BTN_CLASS =
	"flex h-[38px] min-h-[38px] w-full max-w-[160px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] border border-[rgba(22,22,22,0.1)] bg-white px-[10px] py-[5px] pl-[12px] text-[13px] font-medium leading-5 text-[#343434] shadow-[0px_4px_97.1px_0px_rgba(0,0,0,0.05)] transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/20 sm:w-[160px]";

/** @deprecated Figma uses inset action tile — use PORTAL_COURSE_FIELD_ACTION_BTN_CLASS */
export const PORTAL_COURSE_FIELD_ACTION_CLASS = PORTAL_COURSE_FIELD_ACTION_BTN_CLASS;

/** @deprecated use PORTAL_COURSE_FIELD_ACTION_BTN_CLASS */
export const PORTAL_COURSE_FIELD_ACTION_COMPACT_CLASS = PORTAL_COURSE_FIELD_ACTION_BTN_CLASS;

/** @deprecated use PORTAL_COURSE_FIELD_SHELL_CLASS */
export const PORTAL_FIELD_SHELL_CLASS = PORTAL_COURSE_FIELD_SHELL_CLASS;

export const PORTAL_CARD_TITLE_CLASS = "text-[16px] font-bold leading-6 text-gray-900";

export const PORTAL_FORM_FIELD_LABEL_CLASS = "mb-2 block text-[1rem] font-semibold text-gray-700";

/** Matches `InputField` medium / `FORM_CONTROL_HEIGHT` (48px) */
export const PORTAL_FORM_INPUT_CONTROL_HEIGHT_CLASS = "h-[48px] min-h-[48px]";

export const PORTAL_FORM_READONLY_CONTROL_CLASS = `flex ${PORTAL_FORM_INPUT_CONTROL_HEIGHT_CLASS} box-border items-center rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-[13px] text-gray-700`;

export const PORTAL_FORM_NATIVE_INPUT_CLASS = `w-full min-w-0 ${PORTAL_FORM_INPUT_CONTROL_HEIGHT_CLASS} box-border rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-[13px] text-gray-700 placeholder:text-[13px] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent`;

/** Matches `formControlHeight` (48px) on CustomSelect — use px, not `h-12`, so rem root font does not shrink controls */
export const PORTAL_FORM_SELECT_CONTROL_HEIGHT_CLASS = "h-[48px] min-h-[48px]";

export const PORTAL_FORM_SELECT_DISABLED_SHELL_CLASS = `flex ${PORTAL_FORM_SELECT_CONTROL_HEIGHT_CLASS} box-border items-center rounded-[10px] border border-gray-200 bg-gray-50 px-3 text-[13px] text-gray-500`;

export const PORTAL_FORM_SELECT_SKELETON_CONTROL_CLASS = `${PORTAL_FORM_SELECT_CONTROL_HEIGHT_CLASS} rounded-[10px] bg-gray-200`;

/** Set Preferences modal — Add Location selects (slightly shorter than default 48px) */
export const PORTAL_PREFERENCES_SELECT_CONTROL_HEIGHT = 40;

export const PORTAL_PREFERENCES_SELECT_CONTROL_HEIGHT_CLASS = "h-[40px] min-h-[40px]";

export const PORTAL_PREFERENCES_SELECT_DISABLED_SHELL_CLASS = `flex ${PORTAL_PREFERENCES_SELECT_CONTROL_HEIGHT_CLASS} box-border items-center rounded-[10px] border border-gray-200 bg-gray-50 px-3 text-[13px] text-gray-500`;

export const PORTAL_PREFERENCES_SELECT_SKELETON_CONTROL_CLASS = `${PORTAL_PREFERENCES_SELECT_CONTROL_HEIGHT_CLASS} rounded-[10px] bg-gray-200`;

/** View Registration / admission form footer — outline action (Back, Cancel) */
export const PORTAL_ACTION_BTN_OUTLINE_CLASS =
	"min-w-[120px] py-3 px-6 rounded-lg font-semibold transition-colors bg-white text-accent border-2 border-accent hover:bg-accent-40 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed";

/** View Registration / admission form footer — primary action (Submit, Save, Edit) */
export const PORTAL_ACTION_BTN_PRIMARY_CLASS =
	"min-w-[160px] py-3 px-6 rounded-lg font-medium transition-colors whitespace-nowrap bg-accent text-white hover:bg-accent-hover disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed";
